import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm, useWatch } from "react-hook-form";
import LovCanvasModels from "src/components/Lov/CanvasModels";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Tabs from "src/components/Tabs";
import { useTranslation } from "src/context/Translations/context";
import Permissions from "src/components/Permissions";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { simplePermissionType } from "src/components/Permissions/interface";
import { useAuth } from "src/context/Auth/context";
import DeleteModal from "src/components/DeleteModal";
import { ICatbb001, ICatbb003 } from "../../interface";
import { IRefICanvasModals } from "./interface";
import { addAndChangeSchema, deleteSchemaCanvas } from "../../schemas";

interface IComponentModalCanvas{
	callbackChangeCanvas?: () => Promise<void>;
	callbackAddCanvas?: () => Promise<void>;
	callbackDeleteCanvas?: (id: number) => void;
}

const Modals: React.ForwardRefRenderFunction<IRefICanvasModals, IComponentModalCanvas> = ({
	callbackChangeCanvas,
	callbackAddCanvas,
	callbackDeleteCanvas
}, ref) => {

	const { user } = useAuth();
	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();

	const [isAdd, setIsAdd] = useState(false);
	const [selectedCanvas, setSelectedCanvas] = useState<ICatbb001>({} as ICatbb001);

	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const modalDelete = useRef<RCCRefModalProps>(null);

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const { handleSubmit, 
		register , 
		unregister,
		control,
		reset, 
		setError, 
		setValue,
		formState: { errors }, 
		clearErrors
	} = useForm();

	const { 
		handleSubmit : handleSubmitDelete, 
		register : registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const actionButtonAdd = () => {
		reset();
		setIsAdd(true);
		setSelectedCanvas({} as ICatbb001); 

		const initialPermission =
		[{
			cab003Id:0,
			cab003Efa005Id: user.efa005Id,
			efa005Nome: user.efa005Nome,
			cab003Permissao: parseInt(simplePermissionType.VIEW_AND_EDIT, 10) 
		}] as ICatbb003[];

		setValue("catbb003",initialPermission);
		modalAddOrChange.current?.openModal();
	};
  
	const actionButtonChange = (canvas: ICatbb001) => {
		reset();
		setIsAdd(false);
		queryCanvasData(canvas.cab001Id);
	};

	const actionButtonDelete = (canvas: ICatbb001) => {
		resetDelete(); 
		setSelectedCanvas(canvas); 
		modalDelete.current?.openModal();
	};

	const objToNumber = (obj: any) => {
		if(typeof obj === "object")
			return parseInt(obj.value, 10);
		if(typeof obj === "string")
			return parseInt(obj, 10);
		if(typeof obj !== "number")
			return "";
		
		return obj;
	};

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();

			const dataChanged = data;
			dataChanged.cab001Caa001Id = objToNumber(data.cab001Caa001Id);

			if(data.catbb003)
				data.catbb003.forEach((item: any) => {
					const catbb003Changed = item;
					catbb003Changed.cab003Id = item.cab003Id ? parseInt(item.cab003Id, 10) : "";
					catbb003Changed.cab003Efa005Id = item.cab003Efa005Id ? objToNumber(item.cab003Efa005Id) : null;
					catbb003Changed.cab003Efa004Id = item.cab003Efa004Id ? objToNumber(item.cab003Efa004Id) : null;
					catbb003Changed.cab003Permissao = item.cab003Permissao ? objToNumber(item.cab003Permissao)  : null;
				});

			await addAndChangeSchema(translate).validate(data,{
				abortEarly: false
			});
	
			// verifica se tem permissão de Visializar e Editar
			if(!data.catbb003.filter((x: any) => x.cab003Permissao.toString() === simplePermissionType.VIEW_AND_EDIT).length){
				modalAddOrChange.current?.resetLoadingButton();
				addToast({
					color: "yellow",
					description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
					title: translate("Atenção")
				});
				return;
			}

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Canvas/Insert",
					body: {
						...data,
						cab001Caa001Id: parseInt(data.cab001Caa001Id, 10)
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Canvas/Update",
					body: {
						...data,
						cab001Id: selectedCanvas.cab001Id,
						cab001Caa001Id: parseInt(data.cab001Caa001Id, 10)
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Canvas adicionado com sucesso") : translate("Canvas alterado com sucesso"),
				title: translate("Sucesso")
			});

			if(isAdd){
				if(callbackAddCanvas)
					callbackAddCanvas();
			}else if(callbackChangeCanvas)
				callbackChangeCanvas();
				

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar canvas") : translate("Erro ao alterar canvas"), addToast);
		}
	};

	const queryCanvasData = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Canvas/Select",
				urlParameters: {
					id
				}
			});

			setSelectedCanvas(data);
			setLoading(false);
			modalAddOrChange.current?.openModal();

		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}		
	};
		
	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchemaCanvas(translate).validate({...data, "cab001DescricaoConfirmation": selectedCanvas.cab001Descricao},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "Canvas/Delete",
				urlParameters: {
					id: selectedCanvas.cab001Id
				}
			});

			if(callbackDeleteCanvas)
				callbackDeleteCanvas(selectedCanvas.cab001Id);

			addToast({
				title: translate("Sucesso"),
				description: translate("Canvas excluído com sucesso"),
				color: "green"
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();


		}catch(err: any){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;
			standardErrorMessages(err, translate("Erro ao excluir canvas"), addToast);
		}	
	};
	
	const permissionsWatch = useWatch({
		control,
		name: "catbb003"
	});

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de canvas")}
				description= {`${translate("Deseja realmente excluir o canvas")}: ${selectedCanvas.cab001Descricao}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="cab001Descricao"
						label={translate("Digite o nome do canvas para excluí-lo")}
						maxLength={200}
						error={errorsDelete.cab001Descricao}
						required
					/>
				</RCCGrid>
			</DeleteModal>

    	<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Inserção de Canvas") : translate("Alterar Canvas"),
					subtitle: isAdd ? "" : `${translate("Canvas")}: ${selectedCanvas.cab001Descricao}`,
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				onSubmit={handleSubmit(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs 
					name="TabCanvas"
					tabInit={0}
					options={[{
						label: translate("Geral"),
						content: (
							<>
								<RCCGrid
									container
									item
									direction="column"
									alignItems="center"
								>
									<RCCGrid
										container
										item
									>
										<RCCInput 
											register={register}
											name="cab001Descricao"
											label={translate("Título")}
											maxLength={200}
											defaultValue={selectedCanvas.cab001Descricao ?? ""}
											error={errors.cab001Descricao}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
									>
										<LovCanvasModels 
											control={control}
											name="cab001Caa001Id"
											label={translate("Modelo de canvas")}
											defaultValue={ selectedCanvas ? { value: selectedCanvas.cab001Caa001Id, label: selectedCanvas.caa001Descricao } : ""}
											disabled={!isAdd}
											error={errors.cab001Caa001Id}
											required
										/>
									</RCCGrid>
								</RCCGrid>
							</>
						)
					},{
						label: translate("Permissões"),
						content: (
							<>
								<Permissions
									register={register}
									unregister={unregister}
									control={control}
									error={errors}
									clearErrors={clearErrors}
									permissionsNames= {{
										nameObject: "catbb003",
										nameOfID: "cab003Id",
										nameOfSelectUser: "cab003Efa005Id",
										nameOfSelectGroup: "cab003Efa004Id",
										nameOfSelectPermission: "cab003Permissao",
										nameOfSelectGroupDescription:"efa004Descricao",
										nameOfSelectUserDescription:"efa005Nome",
										defaultValue: isAdd ? permissionsWatch: selectedCanvas.catbb003
									}}
									permissionType="simple"
								/>
							</>
						)
					}]}
				/>
			</Modal>
		
		</>
	);
};




export default forwardRef(Modals);
